import type {SuggestAction, SuggestLocation, SuggestObject, SuggestResponse} from '@yandex-delivery/api/model/suggest'
import {
  type LngLat,
  type SuggestResponse as SuggestResponseBase,
  useAddNotification,
  useTransition,
} from '@yandex-delivery/blocks'
import {DEFAULT_CENTER} from '@yandex-delivery/blocks/map/consts'
import type {ServerError} from '@yandex-delivery/core/utils/network'
import {useFormContext} from '@yandex-delivery/form'
import {get, pipe} from '@yandex-delivery/utils'
import {useCallback} from 'react'

import {fetchAddressSuggest} from 'src/shared/api'
import {fromSuggestToPointAddress} from 'src/shared/utils'

import {getAddressCoordinatesFieldName} from './utils/get-address-coordinates-field-name'
import {getAddressFieldName} from './utils/get-address-field-name'

export function useFetchAddressByLocation(location: LngLat = DEFAULT_CENTER) {
  const addNotification = useAddNotification()
  const {setFieldValue} = useFormContext()
  const [{isPending, payload: suggestValue}, startTransition] = useTransition<
    SuggestResponseBase<SuggestResponse, SuggestObject>,
    ServerError
  >(undefined, {
    key: 'fetch-taxi-suggest',
  })

  const lngLatToSuggestLocation = (locationCoords: LngLat): SuggestLocation => {
    return [locationCoords[0], locationCoords[1]]
  }

  const fetchTaxiSuggestHandler = useCallback(
    (coordinates: LngLat, index: number) => {
      const fieldCoordinatesPath = getAddressCoordinatesFieldName(index)
      setFieldValue(fieldCoordinatesPath, coordinates)

      startTransition(
        () =>
          fetchAddressSuggest({
            location: lngLatToSuggestLocation(location),
            action: 'pin_drop' as SuggestAction,
            point: lngLatToSuggestLocation(coordinates),
            type: index === 0 ? 'a' : 'b',
          }),
        {
          onCommit: result => {
            const address = pipe(result, get('suggest[0].__origin'), fromSuggestToPointAddress)

            const fieldPath = getAddressFieldName(index)
            setFieldValue(fieldPath, address)
          },
          onError: error => {
            addNotification(error)
            setFieldValue(fieldCoordinatesPath, undefined)
          },
        },
      )
    },
    [location, addNotification, setFieldValue],
  )

  return {
    isPending,
    suggestValue,
    fetchTaxiSuggestHandler,
  }
}
